import React, { useState } from 'react';
import ProsthesisDashboard from './ProsthesisDashboard';
import ProsthesisOrderList from './ProsthesisOrderList';
import ProsthesisTimeline from './ProsthesisTimeline';
import ProsthesisOrderForm from './ProsthesisOrderForm';
import { prosthesisApi } from '../../services/api';
import { CreateCommandeProtheseData } from '../../types';
import { 
  BarChart3, 
  Package, 
  Clock, 
  Settings,
  ArrowLeft
} from 'lucide-react';

type ProsthesisView = 'dashboard' | 'orders' | 'timeline' | 'order-detail' | 'create-order' | 'edit-order';

interface ProsthesisModuleProps {
  initialView?: ProsthesisView;
}

const ProsthesisModule: React.FC<ProsthesisModuleProps> = ({ 
  initialView = 'dashboard' 
}) => {
  const [currentView, setCurrentView] = useState<ProsthesisView>(initialView);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [createOrderLoading, setCreateOrderLoading] = useState(false);

  const navigationItems = [
    {
      key: 'dashboard' as ProsthesisView,
      label: 'Tableau de bord',
      icon: <BarChart3 className="h-4 w-4" />,
    },
    {
      key: 'orders' as ProsthesisView,
      label: 'Commandes',
      icon: <Package className="h-4 w-4" />,
    },
    {
      key: 'timeline' as ProsthesisView,
      label: 'Timeline',
      icon: <Clock className="h-4 w-4" />,
    },
  ];

  const handleOrderSelect = (orderId: number) => {
    setSelectedOrderId(orderId);
    setCurrentView('order-detail');
  };

  const handleBackToOrders = () => {
    setSelectedOrderId(null);
    setCurrentView('orders');
  };

  const handleCreateOrder = () => {
    setSelectedOrderId(null);
    setCurrentView('create-order');
  };

  const handleEditOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
    setCurrentView('edit-order');
  };

  const handleCreateOrderSubmit = async (data: CreateCommandeProtheseData) => {
    setCreateOrderLoading(true);
    console.log('=== Données envoyées ===');
    console.log(JSON.stringify(data, null, 2));
    try {
      await prosthesisApi.createCommande(data);
      setCurrentView('orders');
      alert('Commande créée avec succès !');
    } catch (error: any) {
      console.error('=== Erreur lors de la création de la commande ===');
      console.error('Error:', error);
      if (error.response) {
        console.error('Status:', error.response.status);
        console.error('Response data:', JSON.stringify(error.response.data, null, 2));
        alert(`Erreur ${error.response.status}: ${JSON.stringify(error.response.data)}`);
      }
    } finally {
      setCreateOrderLoading(false);
    }
  };

  const handleEditOrderSubmit = async (data: CreateCommandeProtheseData) => {
    if (!selectedOrderId) return;

    setCreateOrderLoading(true);
    console.log('=== Modification commande ID:', selectedOrderId);
    console.log(JSON.stringify(data, null, 2));
    try {
      await prosthesisApi.updateCommande(selectedOrderId, data);
      setCurrentView('orders');
      alert('Commande modifiée avec succès !');
    } catch (error: any) {
      console.error('=== Erreur lors de la modification de la commande ===');
      console.error('Error:', error);
      if (error.response) {
        console.error('Status:', error.response.status);
        console.error('Response data:', JSON.stringify(error.response.data, null, 2));
        alert(`Erreur ${error.response.status}: ${JSON.stringify(error.response.data)}`);
      }
    } finally {
      setCreateOrderLoading(false);
    }
  };

  const handleCreateOrderCancel = () => {
    setCurrentView('orders');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <ProsthesisDashboard />;
      
      case 'orders':
        return (
          <ProsthesisOrderList
            onOrderSelect={handleOrderSelect}
            onEditOrder={handleEditOrder}
            onCreateOrder={handleCreateOrder}
          />
        );
      
      case 'create-order':
        return (
          <ProsthesisOrderForm
            onSubmit={handleCreateOrderSubmit}
            onCancel={handleCreateOrderCancel}
            loading={createOrderLoading}
          />
        );

      case 'edit-order':
        return selectedOrderId ? (
          <ProsthesisOrderForm
            orderId={selectedOrderId}
            onSubmit={handleEditOrderSubmit}
            onCancel={handleCreateOrderCancel}
            loading={createOrderLoading}
          />
        ) : (
          <div>Commande non trouvée</div>
        );

      case 'timeline':
        return <ProsthesisTimeline />;
      
      case 'order-detail':
        return selectedOrderId ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToOrders}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour aux commandes
              </button>
            </div>
            <ProsthesisTimeline 
              commandeId={selectedOrderId}
              showAddButton={true}
            />
          </div>
        ) : (
          <div>Commande non trouvée</div>
        );
      
      default:
        return <ProsthesisDashboard />;
    }
  };

  return (
    <div className="flex h-full">
      {/* Navigation latérale */}
      <div className="w-64 bg-white border-r border-gray-200 flex-shrink-0">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Gestion des Prothèses
          </h2>
          <nav className="space-y-1">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setCurrentView(item.key)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors ${
                  currentView === item.key
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {renderCurrentView()}
        </div>
      </div>
    </div>
  );
};

export default ProsthesisModule;